<template>
    <div class="user-info">
        <!-- 基础信息 -->
        <el-card shadow="never" class="info-card">
            <template #header>
                <div class="info-card__header">
                    <span class="info-card__title">基础信息</span>
                </div>
            </template>
            <el-row :gutter="24" align="middle">
                <el-col :xs="24" :sm="8" class="profile-avatar-col">
                    <el-upload
                        class="avatar-upload"
                        :action="fileUploadUrl"
                        :data="{ bucketName: '.avatar' }"
                        :headers="headers"
                        :show-file-list="false"
                        :on-success="uploadAvatarSuccess"
                        accept="image/*"
                    >
                        <div v-if="user.avatar == undefined || user.avatar == ''">
                            <el-avatar :size="140" class="profile-avatar">
                                <el-icon style="font-size: 48px"><UserFilled /></el-icon>
                            </el-avatar>
                        </div>
                        <div v-else>
                            <el-avatar :size="140" class="profile-avatar" :src="currentAvatar()"></el-avatar>
                        </div>
                        <div class="profile-avatar__tip">
                            <el-icon><Camera /></el-icon>
                            <span>&nbsp;点击头像上传图片</span>
                        </div>
                    </el-upload>
                </el-col>
                <el-col :xs="24" :sm="16">
                    <el-form class="profile-form" :label-width="isMobile ? '70px' : '96px'">
                        <el-form-item label="姓名">
                            <el-input v-model="user.realName" maxlength="20" show-word-limit />
                        </el-form-item>
                        <el-form-item label="邮箱">
                            <el-input v-model="user.email" disabled />
                        </el-form-item>
                        <el-form-item label="文件空间">
                            <el-progress :stroke-width="22" :text-inside="true" :percentage="fileConf.spaceUsage ? 100 - fileConf.spaceUsage : 0" :format="format"></el-progress>
                        </el-form-item>
                    </el-form>
                </el-col>
            </el-row>
        </el-card>

        <!-- 登录设备 -->
        <el-card shadow="never" class="info-card">
            <template #header>
                <div class="info-card__header">
                    <span class="info-card__title">登录设备</span>
                </div>
            </template>
            <el-table :data="deviceList" class="device-table" :row-class-name="deviceRowClassName" style="width: 100%">
                <el-table-column label="操作" width="100">
                    <template #default="scope">
                        <el-popover placement="top-start" :width="200" trigger="click">
                            <p>确定下线该设备吗？</p>
                            <div style="text-align: right; margin: 0">
                                <el-button type="danger" size="small" @click="forceLogout(scope.row)">确定</el-button>
                            </div>
                            <template #reference>
                                <el-button type="danger" size="small">下线</el-button>
                            </template>
                        </el-popover>
                    </template>
                </el-table-column>
                <el-table-column prop="ip" label="登录IP地址" min-width="240">
                    <template #default="scope">
                        {{ scope.row.ip }}
                        <el-tag v-if="isCurrentDevice(scope.row)" size="small" class="device-current-tag">当前登录</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="loginTime" label="登录时间" min-width="180"></el-table-column>
                <el-table-column prop="expiryTime" label="过期时间" min-width="180"></el-table-column>
                <el-table-column label="是否过期" width="100">
                    <template #default="scope">
                        <div class="name-wrapper">
                            <div v-if="scope.row.expired">
                                <el-tag size="small" type="danger">已过期</el-tag>
                            </div>
                            <div v-else>
                                <el-tag size="small">正常</el-tag>
                            </div>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- 动态口令 -->
        <el-card shadow="never" class="info-card">
            <template #header>
                <div class="info-card__header">
                    <span class="info-card__title">动态口令</span>
                </div>
            </template>
            <el-row :gutter="24">
                <el-col :xs="24" :sm="14">
                    <p class="totp-desc">‌TOTP（Time-based One-Time Password）是一种基于时间同步的一次性密码算法‌。</p>
                    <p class="totp-desc">它通过客户端和服务器之间的时间比对，每60秒或30秒生成一个新的密码，确保了密码的动态性和不可预测性，从而提高了安全性‌。</p>
                    <el-button type="primary" @click="confirmGenerateTotpQrCode">生成动态口令绑定二维码</el-button>
                    <div class="totp-tips">
                        <div class="totp-tips__title">注意：</div>
                        <ul>
                            <li>生成后，上一次的二维码将自动失效</li>
                            <li>二维码仅显示一次，请扫码完成绑定</li>
                            <li>可使用支持TOTP的应用扫描（如：Microsoft Authenticator）</li>
                        </ul>
                    </div>
                </el-col>
                <el-col :xs="24" :sm="10">
                    <div v-if="totpUri.length > 0" class="totp-qrcode">
                        <qrcode-vue :value="totpUri" size="200" level="H" render-as="svg" />
                    </div>
                </el-col>
            </el-row>
        </el-card>
    </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '../../../utils/request.js';
import { avatarImage } from '../../../utils/users.js';
import { config } from '@/utils/config';
import { getToken } from '@/utils/auth';
import QrcodeVue from 'qrcode.vue';

export default {
    data() {
        return {
            headers: [],
            fileUploadUrl: '',
            user: {},
            fileConf: {},
            tableData: [],
            totpUri: '',
            fullWidth: document.documentElement.clientWidth
        };
    },
    components: {
        QrcodeVue
    },
    computed: {
        // 窄屏（手机等小尺寸设备）下压缩表单标签宽度等展示细节
        isMobile() {
            return this.fullWidth <= 700;
        },
        // 当前登录设备排在首行，便于优先查看
        deviceList() {
            if (!this.$store.state.user.token) {
                return this.tableData;
            }
            const current = [];
            const others = [];
            this.tableData.forEach(row => {
                (this.isCurrentDevice(row) ? current : others).push(row);
            });
            return current.concat(others);
        }
    },
    mounted() {
        this.headers['Access-Token'] = getToken();
        this.fileUploadUrl = config().baseServer + 'f/u';
        this.getCurrentUserInfo();
        this.getLoginUserInfo();
        window.addEventListener('resize', this.handleResize);
        this.handleResize();
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            this.fullWidth = document.documentElement.clientWidth;
        },
        // 是否为当前登录所在设备
        isCurrentDevice(row) {
            const currentToken = this.$store.state.user.token;
            return !!currentToken && row.token == currentToken;
        },
        // 当前登录所在行加粗标识
        deviceRowClassName({ row }) {
            return this.isCurrentDevice(row) ? 'current-device-row' : '';
        },
        currentAvatar() {
            return avatarImage(this.user.avatar);
        },
        uploadAvatarSuccess(response, file, fileList) {
            debugger;
            if (response.code == 0) {
                request({
                    url: '/user/updateCurrentUserInfo',
                    method: 'post',
                    data: { type: 'avatar', value: response.data[0].uname }
                }).then(res => {
                    if (res.code == 0) {
                        this.getCurrentUserInfo();
                    }
                });
            }
        },
        getCurrentUserInfo() {
            request({
                url: '/user/currentUserInfo',
                method: 'post'
            }).then(res => {
                if (res.code == 0) {
                    this.user = res.meta.user;
                    this.fileConf = res.meta.fileConf;
                }
            });
        },
        getLoginUserInfo() {
            request({
                url: '/user/getLoginUserInfo',
                method: 'post'
            }).then(res => {
                if (res.code == 0) {
                    this.tableData = res.data;
                }
            });
        },
        format(percentage) {
            var total = parseInt(this.fileConf.spaceLimit / 1024 / 1024);
            var use = parseInt(this.fileConf.usedSpace / 1024 / 1024);
            var have = total - use;
            return percentage === 100 ? '满' : percentage + '% (剩余：' + have + 'MB)';
        },
        onSubmit() {
            console.log('submit!');
        },
        forceLogout(data) {
            console.log(data);
            request({
                url: '/user/logout',
                method: 'post',
                data: data
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({
                        message: '下线成功',
                        type: 'success',
                        duration: 5 * 1000
                    });
                } else {
                    ElMessage({
                        message: '下线失败',
                        type: 'warning',
                        duration: 5 * 1000
                    });
                }
                this.getLoginUserInfo();
            });
        },
        // 生成前先确认：新二维码会使上一次生成的令牌失效
        confirmGenerateTotpQrCode() {
            ElMessageBox.confirm('生成新的绑定二维码后，上一次生成的动态口令将立即失效，已绑定的令牌需要重新绑定。确认继续吗？', '生成动态口令', {
                type: 'warning',
                confirmButtonText: '确认生成',
                cancelButtonText: '取消'
            })
                .then(() => {
                    this.generateTotpQrCode();
                })
                .catch(() => {
                    // 用户取消，无需处理
                });
        },
        generateTotpQrCode() {
            request({
                url: '/user/createTotpUri',
                method: 'post',
                data: { type: 'totp' }
            }).then(res => {
                if (res.code == 0 && res.meta && res.meta.uri) {
                    this.totpUri = res.meta.uri;
                    ElMessage({
                        message: '生成成功',
                        type: 'success',
                        duration: 5 * 1000
                    });
                } else {
                    ElMessage({
                        message: '生成失败',
                        type: 'warning',
                        duration: 5 * 1000
                    });
                }
            });
        }
    }
};
</script>

<style>
.el-main {
    /* background-color: #e9eef3; */
    /* color: #333; */
    text-align: left;
}
.el-card {
    margin: 10px;
}

/* ===================== 个人信息展示样式 ===================== */
.user-info {
    --ui-border: #eef0f4;
    --ui-text: #1f2d3d;
    --ui-muted: #8a94a6;
    color: var(--ui-text);
}
.user-info .el-card {
    margin: 0 0 12px;
    border: 1px solid var(--ui-border);
    border-radius: 12px;
}
.user-info .el-card__header {
    padding: 14px 18px;
    border-bottom: 1px solid var(--ui-border);
}
.user-info .el-card__body {
    padding: 18px;
}
.info-card__header {
    display: flex;
    align-items: center;
}
.info-card__title {
    position: relative;
    padding-left: 10px;
    font-size: 15px;
    font-weight: 600;
}
.info-card__title::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 3px;
    height: 14px;
    margin-top: -7px;
    border-radius: 2px;
    background: linear-gradient(180deg, #6a9cff, #3f6fff);
}

/* ---------- 基础信息 ---------- */
.profile-avatar-col {
    margin-bottom: 12px;
    text-align: center;
}
.profile-avatar-col .el-upload {
    display: inline-block;
    cursor: pointer;
}
.profile-avatar {
    border: 3px solid #fff;
    box-shadow: 0 6px 18px rgba(31, 45, 61, 0.12);
}
.profile-avatar__tip {
    margin-top: 10px;
    font-size: 12px;
    color: var(--ui-muted);
}
.profile-form .el-form-item:last-child {
    margin-bottom: 0;
}
/* el-form-item__content 是 flex 容器，进度条不显式撑宽会被压缩为 0 宽而不可见 */
.profile-form .el-progress {
    width: 100%;
}

/* ---------- 登录设备 ---------- */
.device-table {
    --el-table-header-bg-color: #f7f9fc;
    font-size: 13px;
}
.device-table th.el-table__cell {
    color: var(--ui-muted);
    font-weight: 600;
}
.device-table .el-table__cell {
    padding: 8px 0;
}
/* 当前登录设备所在行加粗 */
.device-table .el-table__row.current-device-row {
    font-weight: 600;
}
/* IP 后面的当前登录标记 */
.device-current-tag {
    margin-left: 8px;
}

/* ---------- 动态口令 ---------- */
.totp-desc {
    margin: 0 0 10px;
    font-size: 13px;
    line-height: 1.8;
    color: #5b6472;
}
.totp-tips {
    margin-top: 16px;
    padding: 12px 16px;
    border: 1px solid #ffe0e0;
    border-radius: 10px;
    background-color: #fff6f6;
}
.totp-tips__title {
    font-size: 13px;
    font-weight: 600;
    color: #d94b4b;
}
.totp-tips ul {
    margin: 6px 0 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 1.9;
    color: #d94b4b;
}
.totp-qrcode {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    border: 1px dashed var(--ui-border);
    border-radius: 12px;
    background-color: #fafbfd;
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 700px) {
    .user-info .el-card__header {
        padding: 12px 14px;
    }
    .user-info .el-card__body {
        padding: 14px;
    }
    .totp-desc {
        font-size: 12px;
    }
}
</style>
